import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterOption } from 'models/Filters';
import { getHasElementsInArray } from 'utils';
import { useWaitingRoomFilterOptions } from '../../../hooks/useWaitingRoom';
import {
  LAST_SEEN_FILTER,
  LAST_SEEN_FILTER_COPY,
} from '../../../subrouters/Members/pages/Members/components/MembersFlatTable/data';
import {
  TYPE_FILTER_VALUES,
  WAITING_ROOM_FILTER_PARAMS,
} from '../components/WaitingRoomTable/data';

// The queue's filters, in the order the table shows the columns they belong to.
// Filtering happens on the BE, so these only ever write query params —
// `useWaitingRoomParams` is what turns them into a request.
export default function useWaitingRoomFilters() {
  const { t } = useTranslation();

  const { requestors, emails, requestingAccessTo } =
    useWaitingRoomFilterOptions();

  // A filter with nothing to pick from is left out rather than offered as an
  // empty dropdown: a queue of secure link requests from people without an
  // account has no requester names to filter by.
  const getValueFilter = useCallback(
    (param: string, label: string, values: string[]): FilterOption[] =>
      getHasElementsInArray(values)
        ? [
            {
              value: param,
              label: t(label),
              isMultiple: true,
              // The values are names and email addresses the BE resolved, so
              // they are their own labels, and long enough to want a search box.
              isSearchable: true,
              options: values.map((value) => ({ value, label: value })),
            },
          ]
        : [],
    [t],
  );

  return useMemo<FilterOption[]>(
    () => [
      ...getValueFilter(
        WAITING_ROOM_FILTER_PARAMS.REQUESTORS,
        'requestor',
        requestors,
      ),
      ...getValueFilter(WAITING_ROOM_FILTER_PARAMS.EMAILS, 'email', emails),
      // Type is the one filter whose options the FE already knows: they are the
      // two pills the table shows, not the four request types behind them.
      {
        value: WAITING_ROOM_FILTER_PARAMS.TYPES,
        label: t('type'),
        isMultiple: true,
        // Buy-side before secure link, matching the order the copy reads in.
        shouldSortOptions: false,
        options: Object.keys(TYPE_FILTER_VALUES).map((value) => ({
          value,
          label: t(value),
        })),
      },
      ...getValueFilter(
        WAITING_ROOM_FILTER_PARAMS.REQUESTING_ACCESS_TO,
        'requestingAccessTo',
        requestingAccessTo,
      ),
      // How long someone has been waiting, offered as the same windows the
      // members table filters last seen by. One at a time: the windows nest, so
      // picking several would only ever mean the widest of them.
      {
        value: WAITING_ROOM_FILTER_PARAMS.REQUESTED,
        label: t('requested'),
        // Shortest window first, which is not alphabetical order.
        shouldSortOptions: false,
        options: Object.values(LAST_SEEN_FILTER).map((value) => ({
          value,
          label: t(LAST_SEEN_FILTER_COPY[value]),
        })),
      },
    ],
    [getValueFilter, requestors, emails, requestingAccessTo, t],
  );
}
